import { useParams } from "react-router-dom";
import { Link } from "@/lib/link";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, MessageCircle, Send, ArrowLeft, ArrowRight, CalendarDays, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import ScrollReveal from "@/components/common/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { getCategory } from "@/data/products";
import { FORMS } from "@/constants/testIds";
import { postForm } from "@/lib/api";

export default function ProductCategory() {
    const { slug } = useParams();
    const { t } = useTranslation();
    const category = getCategory(slug);
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", country: "", inquiry_type: "quotation", quantity: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(category?.image || "");

    if (!category) {
        return (
            <div className="container-x pt-40 pb-24 text-center">
                <h1 className="text-3xl font-extrabold text-[#0A2540]">{t("productCategory.notFound")}</h1>
                <Link to="/products" className="mt-4 inline-block text-[#1D4ED8] font-semibold">{t("productCategory.backToCatalog")}</Link>
            </div>
        );
    }

    const localizedCategory = {
        ...category,
        name: t(category.nameKey),
        tagline: t(category.taglineKey),
        description: t(category.descriptionKey),
        subCategories: category.subCategories.map((key) => t(key)),
        specs: category.specs.map((spec) => ({
            ...spec,
            label: t(spec.labelKey),
            value: t(spec.valueKey),
        })),
    };

    const galleryImages = localizedCategory.galleryImages?.length ? localizedCategory.galleryImages : [localizedCategory.image];
    const heroActions = [
        {
            key: "quote",
            label: t("productCategory.actions.quote"),
            href: "#inquiry",
            className: "inline-flex items-center justify-center rounded-full bg-white text-[#0A2540] hover:bg-slate-100 font-semibold px-6 py-4 min-h-[48px] text-sm sm:text-base shadow-sm",
        },
        {
            key: "specialist",
            label: t("productCategory.actions.specialist"),
            href: `https://wa.me/919918000070?text=Hi%20Exportfy%2C%20I%27m%20interested%20in%20${encodeURIComponent(localizedCategory.name)}.`,
            className: "inline-flex items-center justify-center rounded-full border border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold px-6 py-4 min-h-[48px] text-sm sm:text-base",
            icon: <MessageCircle className="mr-2 w-4 h-4" />,
        },
        {
            key: "consultation",
            label: t("productCategory.actions.consultation"),
            href: "/consultation",
            className: "inline-flex items-center justify-center rounded-full border border-white/25 bg-white/10 text-white hover:bg-white/20 font-semibold px-6 py-4 min-h-[48px] text-sm sm:text-base",
            icon: <CalendarDays className="mr-2 w-4 h-4" />,
        },
    ];

    const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: localizedCategory.name,
        image: localizedCategory.image,
        description: localizedCategory.description,
        brand: { "@type": "Brand", name: "Exportfy.world" },
        category: localizedCategory.name,
    };
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://exportfy.world/" },
            { "@type": "ListItem", position: 2, name: "Products", item: "https://exportfy.world/products" },
            { "@type": "ListItem", position: 3, name: localizedCategory.name, item: `https://exportfy.world/products/${localizedCategory.slug}` },
        ],
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postForm("/product-inquiry", { ...form, product_slug: category.slug });
            toast.success(t("toast.inquiryReceived"));
            setForm({ name: "", email: "", phone: "", company: "", country: "", inquiry_type: "quotation", quantity: "", message: "" });
        } catch (err) {
            toast.error(t("toast.inquiryError"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title={`${localizedCategory.name} — Export from India | Exportfy.world`}
                description={localizedCategory.description}
                path={`/products/${localizedCategory.slug}`}
                image={localizedCategory.image}
                schema={[productSchema, breadcrumbSchema]}
            />

            {/* Hero */}
            <section className="relative overflow-hidden bg-[#061A2E]">
                <div className="absolute inset-0">
                    <img src={localizedCategory.image} alt={localizedCategory.name} className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#061A2E]/60 to-[#0A2540]/95" />
                </div>
                <div className="relative container-x pt-36 pb-20 sm:pt-40 sm:pb-24">
                    <Breadcrumb className="mb-6">
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/" className="text-white/60 hover:text-white">{t("productCategory.breadcrumbHome")}</Link></BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator className="text-white/30" />
                            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/products" className="text-white/60 hover:text-white">{t("productCategory.breadcrumbProducts")}</Link></BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator className="text-white/30" />
                            <BreadcrumbItem><BreadcrumbPage className="text-white">{localizedCategory.name}</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                    <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-8 xl:gap-10 items-start">
                        <div className="max-w-3xl">
                            <div className="flex flex-wrap gap-2 mb-5">
                                {localizedCategory.oem && <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-100 bg-emerald-500/20 border border-emerald-400/30 rounded-full px-2.5 py-1">{t("productCategory.badgeOem")}</span>}
                                {localizedCategory.privateLabel && <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-100 bg-amber-500/20 border border-amber-400/30 rounded-full px-2.5 py-1">{t("productCategory.badgePrivateLabel")}</span>}
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-white/80 bg-white/10 border border-white/20 rounded-full px-2.5 py-1">{t("productCategory.badgeOrigin")}</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">{localizedCategory.name}</h1>
                            <p className="mt-5 text-lg text-white/75 leading-relaxed">{localizedCategory.tagline}</p>
                            <div className="mt-8 flex flex-wrap items-stretch gap-3">
                                {heroActions.map((action) => (
                                    action.href.startsWith("http") ? (
                                        <a key={action.key} href={action.href} target="_blank" rel="noopener noreferrer" className={action.className}>
                                            {action.icon}
                                            {action.label}
                                        </a>
                                    ) : action.href.startsWith("#") ? (
                                        <a key={action.key} href={action.href} className={action.className}>
                                            {action.label}
                                        </a>
                                    ) : (
                                        <Link key={action.key} to={action.href} className={action.className}>
                                            {action.icon}
                                            {action.label}
                                        </Link>
                                    )
                                ))}
                            </div>
                        </div>
                        <div className="rounded-[28px] border border-white/15 bg-white/10 p-3 shadow-2xl shadow-slate-950/20 backdrop-blur">
                            <img src={selectedImage || galleryImages[0]} alt={localizedCategory.name} className="h-72 w-full rounded-[22px] object-cover" />
                            <div className="mt-3 grid grid-cols-3 gap-2">
                                {galleryImages.map((image, index) => (
                                    <button key={`${image}-${index}`} type="button" onClick={() => setSelectedImage(image)} className={`overflow-hidden rounded-xl border ${selectedImage === image ? "border-white ring-2 ring-white/60" : "border-white/15"}`}>
                                        <img src={image} alt={`${localizedCategory.name} view ${index + 1}`} className="h-20 w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Description + Specs */}
            <section className="section-y">
                <div className="container-x grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-14">
                    <div className="space-y-8">
                        <ScrollReveal>
                            <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
                                <div className="flex items-center gap-3">
                                    <div className="rounded-2xl bg-[#DBEAFE] p-3 text-[#2563EB]">
                                        <Sparkles className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#1D4ED8]">{t("productCategory.overview")}</p>
                                        <h2 className="text-2xl font-extrabold text-[#0A2540] tracking-tight">{localizedCategory.name}</h2>
                                    </div>
                                </div>
                                <p className="mt-6 text-slate-600 leading-relaxed text-lg">{localizedCategory.description}</p>
                                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                                    <div className="rounded-2xl bg-slate-50 p-4">
                                        <p className="text-sm font-semibold text-[#0A2540]">MOQ flexibility</p>
                                        <p className="mt-1 text-sm text-slate-600">Small trial orders and scalable production runs available.</p>
                                    </div>
                                    <div className="rounded-2xl bg-slate-50 p-4">
                                        <p className="text-sm font-semibold text-[#0A2540]">Factory support</p>
                                        <p className="mt-1 text-sm text-slate-600">Sampling, QC, and packaging guidance handled end to end.</p>
                                    </div>
                                    <div className="rounded-2xl bg-slate-50 p-4">
                                        <p className="text-sm font-semibold text-[#0A2540]">Delivery readiness</p>
                                        <p className="mt-1 text-sm text-slate-600">Lead times and logistics are reviewed before confirmation.</p>
                                    </div>
                                </div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal delay={80}>
                            <div className="rounded-[28px] border border-slate-200 bg-[#F8FAFC] p-8 shadow-sm">
                                <h3 className="text-xl font-bold text-[#0A2540]">{t("productCategory.subCategories")}</h3>
                                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                    {localizedCategory.subCategories.map((sc) => (
                                        <div key={sc} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="h-2.5 w-2.5 rounded-full bg-[#1D4ED8]" />
                                                <span className="font-semibold text-[#0A2540]">{sc}</span>
                                            </div>
                                            <p className="mt-2 text-sm text-slate-500">{t("productCategory.subCategoryHint")}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal delay={120}>
                            <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
                                <h3 className="text-xl font-bold text-[#0A2540]">{t("productCategory.specifications")}</h3>
                                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                    {localizedCategory.specs.map((s) => (
                                        <div key={s.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="text-sm font-bold text-[#0A2540] uppercase tracking-wider">{s.label}</div>
                                            <div className="mt-2 text-slate-600">{s.value}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal delay={160}>
                            <div className="rounded-[28px] bg-[#F8FAFC] border border-slate-200 p-6 flex items-start gap-4">
                                <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-bold text-[#0A2540]">{t("productCategory.disciplineTitle")}</div>
                                    <p className="mt-1 text-slate-600 text-sm">{t("productCategory.disciplineText")}</p>
                                </div>
                            </div>
                        </ScrollReveal>
                    </div>

                    {/* Inquiry form */}
                    <div id="inquiry" className="lg:sticky lg:top-28 h-fit space-y-5">
                        <ScrollReveal>
                            <div className="rounded-[28px] bg-gradient-to-br from-[#0A2540] to-[#1D4ED8] p-6 text-white shadow-xl">
                                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-100">{t("productCategory.support")}</p>
                                <h3 className="mt-3 text-2xl font-extrabold">Need a tailored sourcing plan?</h3>
                                <p className="mt-2 text-sm text-blue-50/90">Share your spec, target price, and timeline to get matched with the right sourcing specialist.</p>
                                <div className="mt-5 space-y-3">
                                    <a href="#inquiry" className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold hover:bg-white/20">
                                        <span>{t("productCategory.actions.quote")}</span>
                                        <ArrowRight className="w-4 h-4" />
                                    </a>
                                    <a href={`https://wa.me/919918000070?text=Hi%20Exportfy%2C%20I%27m%20interested%20in%20${encodeURIComponent(localizedCategory.name)}.`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold hover:bg-white/20">
                                        <span>{t("productCategory.actions.specialist")}</span>
                                        <MessageCircle className="w-4 h-4" />
                                    </a>
                                    <Link to="/consultation" className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold hover:bg-white/20">
                                        <span>{t("productCategory.actions.consultation")}</span>
                                        <CalendarDays className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal>
                            <form onSubmit={submit} className="bg-white rounded-[28px] border border-slate-200 shadow-lg shadow-slate-900/5 p-7">
                                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-widest px-3 py-1">
                                    <Sparkles className="w-3.5 h-3.5" /> {t("productCategory.reply")}
                                </div>
                                <h3 className="mt-4 text-2xl font-extrabold text-[#0A2540]">{t("productCategory.inquiryTitle", { name: localizedCategory.name })}</h3>
                                <p className="mt-2 text-sm text-slate-500">{t("productCategory.inquirySubtitle")}</p>
                                <div className="mt-6 space-y-3">
                                    <Input required placeholder={t("productCategory.form.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={FORMS.productInquiry.name} />
                                    <Input required type="email" placeholder={t("productCategory.form.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={FORMS.productInquiry.email} />
                                    <Input placeholder={t("productCategory.form.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={FORMS.productInquiry.phone} />
                                    <Input placeholder={t("productCategory.form.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid={FORMS.productInquiry.company} />
                                    <Input placeholder={t("productCategory.form.country")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid={FORMS.productInquiry.country} />
                                    <Select value={form.inquiry_type} onValueChange={(v) => setForm({ ...form, inquiry_type: v })}>
                                        <SelectTrigger data-testid={FORMS.productInquiry.type}><SelectValue placeholder={t("productCategory.form.type")} /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="sample">{t("productCategory.typeSample")}</SelectItem>
                                            <SelectItem value="quotation">{t("productCategory.typeQuotation")}</SelectItem>
                                            <SelectItem value="general">{t("productCategory.typeGeneral")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Input placeholder={t("productCategory.form.quantity")} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid={FORMS.productInquiry.quantity} />
                                    <Textarea rows={3} placeholder={t("productCategory.form.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid={FORMS.productInquiry.message} />
                                </div>
                                <Button type="submit" disabled={loading} data-testid={FORMS.productInquiry.submit} className="mt-5 w-full rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold py-6">
                                    {loading ? t("productCategory.form.sending") : (<>{t("productCategory.form.submit")} <Send className="ml-2 w-4 h-4" /></>)}
                                </Button>
                            </form>
                        </ScrollReveal>
                    </div>
                </div>
            </section>

            <section className="section-y bg-[#F8FAFC]">
                <div className="container-x">
                    <Link to="/products" className="inline-flex items-center gap-2 text-[#1D4ED8] font-semibold"><ArrowLeft className="w-4 h-4" /> {t("productCategory.backToCatalog")}</Link>
                </div>
            </section>
        </>
    );
}
